import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, limit, onSnapshot, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip, Avatar, Tooltip,
  IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, Divider, Grid, Stack, Card, CardContent,
  CircularProgress, MenuItem, useTheme, useMediaQuery
} from '@mui/material';
import {
  Security, AllInclusive, Edit, Search, ArrowBack,
  Person, PersonOff, History, Refresh, WhatsApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function Admin({ user }) {
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('todos');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState('');

  const [openEdit, setOpenEdit] = useState(false);
  const [userSelecionado, setUserSelecionado] = useState(null);
  const [editData, setEditData] = useState({ nome: '', email: '', expiraEm: '', status: 'ativo' });

  const [openDetails, setOpenDetails] = useState(false);
  const [detailsUser, setDetailsUser] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [botStatus, setBotStatus] = useState(null);
  const [botStatusError, setBotStatusError] = useState('');
  const [billingConfig, setBillingConfig] = useState({ plans: {} });
  const [savingPlans, setSavingPlans] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const ADMIN_EMAIL = 'lucas.nogueira.teixeira18@gmail.com';
  const defaultPlanConfig = {
    mensal: { label: 'Mensal', pricePerMonth: 20, cycleMonths: 1, highlight: false },
    trimestral: { label: 'Trimestral', pricePerMonth: 18, cycleMonths: 3, highlight: false, subtitle: 'Economize 10%' },
    semestral: { label: 'Semestral', pricePerMonth: 16, cycleMonths: 6, highlight: true, subtitle: 'Melhor custo/benefício' },
    anual: { label: 'Anual', pricePerMonth: 14, cycleMonths: 12, highlight: false, subtitle: 'Menor valor/mês' }
  };

  const formatDate = (value) => {
    if (!value) return '---';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '---';
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (value) => {
    if (!value) return '---';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '---';
    return date.toLocaleString('pt-BR');
  };

  const isVencido = (u) => {
    if (!u?.expiraEm || u?.status === 'vitalicio') return false;
    const expira = new Date(u.expiraEm);
    if (Number.isNaN(expira.getTime())) return false;
    return expira < new Date();
  };

  const statusVisual = (u) => {
    if (u?.status === 'bloqueado') return { label: 'Bloqueado', color: 'error' };
    if (u?.status === 'vitalicio') return { label: 'Vitalicio', color: 'success' };
    if (isVencido(u)) return { label: 'Vencido', color: 'warning' };
    if (u?.status === 'trial') return { label: 'Trial', color: 'info' };
    return { label: 'Ativo', color: 'success' };
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      lista.sort((a, b) => (a.nome || a.email || '').localeCompare(b.nome || b.email || '', 'pt-BR'));
      setUsuarios(lista);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchUsers();
      loadBillingConfig();
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) return undefined;

    const botRef = doc(db, 'integrations', 'whatsapp_bot');
    const unsubscribe = onSnapshot(
      botRef,
      (snap) => {
        setBotStatusError('');
        setBotStatus(snap.exists() ? snap.data() : null);
      },
      (error) => {
        setBotStatus(null);
        setBotStatusError(error?.message || 'Erro ao ler status do bot.');
      }
    );

    return () => unsubscribe();
  }, [user?.email]);

  const buildFallbackLogs = (u) => {
    const eventos = [];
    if (u?.criadoEm) eventos.push({ id: 'criado', acao: 'Conta criada', data: u.criadoEm });
    if (u?.ultimoLoginEm) eventos.push({ id: 'login', acao: 'Ultimo login', data: u.ultimoLoginEm });
    if (u?.ultimoLogoutEm) eventos.push({ id: 'logout', acao: 'Ultimo logout', data: u.ultimoLogoutEm });
    if (u?.atualizadoEm) eventos.push({ id: 'atualizado', acao: 'Dados atualizados', data: u.atualizadoEm });
    if (u?.expiraEm) eventos.push({ id: 'expiracao', acao: 'Data de expiracao da assinatura', data: u.expiraEm });
    return eventos.sort((a, b) => new Date(b.data) - new Date(a.data));
  };

  const loadBillingConfig = async () => {
    try {
      const snap = await getDoc(doc(db, 'billing_config', 'general'));
      if (snap.exists()) {
        setBillingConfig(snap.data());
      } else {
        setBillingConfig({ plans: defaultPlanConfig });
      }
    } catch (error) {
      console.warn('Erro ao carregar config de planos:', error);
      setBillingConfig({ plans: defaultPlanConfig });
    }
  };

  const saveBillingConfig = async () => {
    setSavingPlans(true);
    try {
      await setDoc(
        doc(db, 'billing_config', 'general'),
        {
          plans: billingConfig.plans,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || 'admin'
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Erro ao salvar planos:', error);
    } finally {
      setSavingPlans(false);
    }
  };

  const abrirDetalhes = async (u) => {
    setDetailsUser(u);
    setOpenDetails(true);
    setLoadingLogs(true);
    try {
      let logs = [];

      try {
        const qLogs = query(collection(db, `users/${u.id}/logs`), orderBy('data', 'desc'), limit(40));
        const logsSnap = await getDocs(qLogs);
        logs = logsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        logs = [];
      }

      if (logs.length === 0) {
        try {
          const qAcessos = query(collection(db, `users/${u.id}/acessos`), orderBy('data', 'desc'), limit(40));
          const acessosSnap = await getDocs(qAcessos);
          logs = acessosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
          logs = [];
        }
      }

      if (logs.length === 0) {
        logs = buildFallbackLogs(u);
      }

      setUserLogs(logs);
    } finally {
      setLoadingLogs(false);
    }
  };

  const abrirEditor = (u) => {
    setUserSelecionado(u);
    setEditData({
      nome: u.nome || '',
      email: u.email || '',
      expiraEm: u.expiraEm ? u.expiraEm.split('T')[0] : '',
      status: u.status || 'ativo'
    });
    setOpenEdit(true);
  };

  const syncAuthStatus = async (uid, disabled) => {
    try {
      const toggle = httpsCallable(functions, 'toggleUserDisabled');
      await toggle({ uid, disabled });
    } catch (error) {
      console.error('Erro ao atualizar status de login:', error);
    }
  };

  const gerenciarAcesso = async (u, { dias = 0, vitalicio = false, bloquear = false, desbloquear = false }) => {
    setActionLoadingId(u.id);
    try {
      let novaData = new Date();
      let novoStatus = u.status || 'ativo';
      let disabled = null;

      if (vitalicio) {
        novaData = new Date('2099-12-31T23:59:59');
        novoStatus = 'vitalicio';
        disabled = false;
      } else if (bloquear) {
        novoStatus = 'bloqueado';
        disabled = true;
      } else if (desbloquear) {
        const dataAtual = u.expiraEm ? new Date(u.expiraEm) : new Date();
        novaData = dataAtual > new Date() ? dataAtual : new Date();
        novaData.setDate(novaData.getDate() + 30);
        novoStatus = 'ativo';
        disabled = false;
      } else {
        const dataBase = (u.expiraEm && new Date(u.expiraEm) > new Date()) ? new Date(u.expiraEm) : new Date();
        novaData = new Date(dataBase);
        novaData.setDate(novaData.getDate() + dias);
        novoStatus = u.status === 'bloqueado' ? 'ativo' : (u.status || 'ativo');
        disabled = false;
      }

      const payload = {
        status: novoStatus,
        atualizadoEm: new Date().toISOString()
      };

      if (!bloquear) {
        payload.expiraEm = novaData.toISOString();
      }

      await updateDoc(doc(db, 'users', u.id), payload);

      if (disabled !== null) {
        await syncAuthStatus(u.id, disabled);
      }

      await fetchUsers();
    } finally {
      setActionLoadingId('');
    }
  };

  const salvarEdicaoManual = async () => {
    if (!userSelecionado) return;

    setActionLoadingId(userSelecionado.id);
    try {
      const payload = {
        nome: editData.nome,
        email: editData.email,
        status: editData.status,
        atualizadoEm: new Date().toISOString()
      };

      if (editData.expiraEm) {
        payload.expiraEm = new Date(`${editData.expiraEm}T23:59:59`).toISOString();
      }

      await updateDoc(doc(db, 'users', userSelecionado.id), payload);

      if (editData.status === 'bloqueado') {
        await syncAuthStatus(userSelecionado.id, true);
      } else {
        await syncAuthStatus(userSelecionado.id, false);
      }

      setOpenEdit(false);
      await fetchUsers();
    } finally {
      setActionLoadingId('');
    }
  };

  const estatisticas = (() => {
    const total = usuarios.length;
    const bloqueados = usuarios.filter((u) => u.status === 'bloqueado').length;
    const vencidos = usuarios.filter((u) => isVencido(u)).length;
    const ativos = usuarios.filter((u) => statusVisual(u).label === 'Ativo').length;
    return { total, bloqueados, vencidos, ativos };
  })();

  const usuariosFiltrados = usuarios.filter((u) => {
    const texto = `${u.nome || ''} ${u.email || ''}`.toLowerCase();
    const bateBusca = texto.includes(filtro.toLowerCase());
    if (!bateBusca) return false;

    if (statusFiltro === 'todos') return true;
    if (statusFiltro === 'bloqueado') return u.status === 'bloqueado';
    if (statusFiltro === 'vencido') return isVencido(u);
    if (statusFiltro === 'trial') return u.status === 'trial';
    if (statusFiltro === 'vitalicio') return u.status === 'vitalicio';
    if (statusFiltro === 'ativo') return statusVisual(u).label === 'Ativo';

    return true;
  });

  const botStatusVisual = (() => {
    const status = botStatus?.status || 'offline';
    if (status === 'ready') return { label: 'Conectado', color: 'success' };
    if (status === 'qr') return { label: 'Aguardando QR', color: 'warning' };
    if (status === 'connecting') return { label: 'Conectando', color: 'info' };
    if (status === 'error' || status === 'auth_failure') return { label: 'Erro', color: 'error' };
    if (status === 'disconnected') return { label: 'Desconectado', color: 'default' };
    return { label: 'Offline', color: 'default' };
  })();

  if (user?.email !== ADMIN_EMAIL) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
        <Security sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold">Acesso restrito</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2, color: '#1a237e', fontWeight: 'bold' }}>
        Voltar
      </Button>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, border: '1px solid #e4e8f3' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#1a237e">Admin - Gestao de clientes</Typography>
            <Typography variant="body2" color="text.secondary">Controle de acesso, assinatura e historico de atividade</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              placeholder="Buscar cliente"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            />
            <TextField
              select
              size="small"
              value={statusFiltro}
              onChange={(e) => setStatusFiltro(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="ativo">Ativos</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="vencido">Vencidos</MenuItem>
              <MenuItem value="bloqueado">Bloqueados</MenuItem>
              <MenuItem value="vitalicio">Vitalicios</MenuItem>
            </TextField>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchUsers}>Atualizar</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, border: '1px solid #d6e4ff', bgcolor: '#f8fbff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WhatsApp fontSize="small" /> Bot WhatsApp + Gemini
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Integra a conversa do WhatsApp com cadastro de movimentacoes no banco.
            </Typography>
          </Box>
          <Chip label={botStatusVisual.label} color={botStatusVisual.color} />
        </Stack>

        <Grid container spacing={1.5} sx={{ mt: 0.2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Modelo IA</Typography>
            <Typography variant="body2" fontWeight="bold">{botStatus?.model || 'gemini-2.5-flash'}</Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: 'block' }}>Numero conectado</Typography>
            <Typography variant="body2" fontWeight="bold">{botStatus?.connectedNumber || '---'}</Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: 'block' }}>Ultimo evento</Typography>
            <Typography variant="body2">{formatDateTime(botStatus?.lastEventAt)}</Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: 'block' }}>Comando para iniciar o bot</Typography>
            <Paper variant="outlined" sx={{ mt: 0.5, p: 1, bgcolor: 'white', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap' }}>
                cd whatsapp-bot{'\n'}npm install{'\n'}npm start
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            {botStatus?.status === 'qr' && botStatus?.qrDataUrl ? (
              <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                <Typography variant="caption" color="text.secondary">Leia o QR com o WhatsApp para conectar</Typography>
                <Box
                  component="img"
                  src={botStatus.qrDataUrl}
                  alt="QR Code WhatsApp"
                  sx={{ mt: 1, width: 220, maxWidth: '100%', p: 1, borderRadius: 2, bgcolor: 'white', border: '1px solid #cfe0ff' }}
                />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'white' }}>
                <Typography variant="body2" color="text.secondary">
                  Quando o bot estiver rodando e nao autenticado, o QR sera exibido aqui em tempo real.
                </Typography>
                {botStatusError && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                    Erro Firestore: {botStatusError}
                  </Typography>
                )}
                {botStatus?.lastError && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                    Erro: {botStatus.lastError}
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, border: '1px solid #e4e8f3' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="#1a237e">Planos e preços (Mercado Pago)</Typography>
            <Typography variant="body2" color="text.secondary">Atualize valores que aparecerão para os clientes.</Typography>
          </Box>
          <Button variant="contained" onClick={saveBillingConfig} disabled={savingPlans}>
            {savingPlans ? 'Salvando...' : 'Salvar planos'}
          </Button>
        </Stack>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {Object.entries(billingConfig.plans || defaultPlanConfig).map(([id, plan]) => (
            <Grid item xs={12} sm={6} md={3} key={id}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography fontWeight="bold">{plan.label || id}</Typography>
                  <TextField
                    size="small"
                    label="Valor por mês (R$)"
                    type="number"
                    inputProps={{ step: '0.5', min: 0 }}
                    value={plan.pricePerMonth ?? plan.price ?? ''}
                    onChange={(e) =>
                      setBillingConfig((prev) => ({
                        ...prev,
                        plans: {
                          ...prev.plans,
                          [id]: { ...plan, pricePerMonth: Number(e.target.value) }
                        }
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="Meses do ciclo"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={plan.cycleMonths ?? plan.months ?? 1}
                    onChange={(e) =>
                      setBillingConfig((prev) => ({
                        ...prev,
                        plans: {
                          ...prev.plans,
                          [id]: { ...plan, cycleMonths: Number(e.target.value) }
                        }
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="Subtítulo"
                    value={plan.subtitle || ''}
                    onChange={(e) =>
                      setBillingConfig((prev) => ({
                        ...prev,
                        plans: {
                          ...prev.plans,
                          [id]: { ...plan, subtitle: e.target.value }
                        }
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="Destaque? (true/false)"
                    value={plan.highlight ? 'true' : 'false'}
                    onChange={(e) =>
                      setBillingConfig((prev) => ({
                        ...prev,
                        plans: {
                          ...prev.plans,
                          [id]: { ...plan, highlight: e.target.value === 'true' }
                        }
                      }))
                    }
                  />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e5e7eb' }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" fontWeight="bold">{estatisticas.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #d1fae5' }}>
            <Typography variant="caption" color="text.secondary">Ativos</Typography>
            <Typography variant="h6" fontWeight="bold" color="success.main">{estatisticas.ativos}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #fee2e2' }}>
            <Typography variant="caption" color="text.secondary">Bloqueados</Typography>
            <Typography variant="h6" fontWeight="bold" color="error.main">{estatisticas.bloqueados}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #fef3c7' }}>
            <Typography variant="caption" color="text.secondary">Vencidos</Typography>
            <Typography variant="h6" fontWeight="bold" color="warning.main">{estatisticas.vencidos}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {loadingUsers ? (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }}>Carregando clientes...</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {usuariosFiltrados.map((u) => {
            const visual = statusVisual(u);
            const emAcao = actionLoadingId === u.id;
            return (
              <Card key={u.id} sx={{ borderRadius: 3, border: '1px solid #e4e8f3' }}>
                <CardContent>
                  <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
                    <Box onClick={() => abrirDetalhes(u)} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: 1, cursor: 'pointer' }}>
                      <Avatar sx={{ bgcolor: '#00e676', color: '#0a1929', width: 36, height: 36 }}>{(u.nome || u.email || 'U')[0].toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{u.nome || 'Sem nome'}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email || 'Sem e-mail'}</Typography>
                      </Box>
                    </Box>
                    <Chip label={visual.label} color={visual.color} size="small" />
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ mt: 1.2 }}>
                    <Typography variant="caption" color="text.secondary">Vence: <strong>{formatDate(u.expiraEm)}</strong></Typography>
                    <Typography variant="caption" color="text.secondary">Ultimo login: <strong>{formatDate(u.ultimoLoginEm)}</strong></Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.7} flexWrap="wrap" sx={{ mt: 1.2 }}>
                    <Button size="small" variant="outlined" onClick={() => gerenciarAcesso(u, { dias: 7 })} disabled={emAcao}>+7d</Button>
                    <Button size="small" variant="outlined" onClick={() => gerenciarAcesso(u, { dias: 30 })} disabled={emAcao}>+30d</Button>
                    <Button size="small" variant="outlined" onClick={() => gerenciarAcesso(u, { dias: 365 })} disabled={emAcao}>+1a</Button>
                    <Button size="small" variant="outlined" color="success" onClick={() => gerenciarAcesso(u, { vitalicio: true })} disabled={emAcao}>Vitalicio</Button>
                    {u.status === 'bloqueado' ? (
                      <Button size="small" variant="outlined" color="success" onClick={() => gerenciarAcesso(u, { desbloquear: true })} disabled={emAcao}>Desbloquear</Button>
                    ) : (
                      <Button size="small" variant="outlined" color="error" onClick={() => gerenciarAcesso(u, { bloquear: true })} disabled={emAcao}>Bloquear</Button>
                    )}
                    <Button size="small" variant="outlined" onClick={() => abrirEditor(u)} startIcon={<Edit />} disabled={emAcao}>Editar</Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e4e8f3' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#1a237e' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Vencimento</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ultimo acesso</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuariosFiltrados.map((u) => {
                const visual = statusVisual(u);
                const emAcao = actionLoadingId === u.id;
                return (
                  <TableRow key={u.id} hover>
                    <TableCell onClick={() => abrirDetalhes(u)} sx={{ cursor: 'pointer' }}>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#00e676', color: '#0a1929', width: 36, height: 36 }}>{(u.nome || u.email || 'U')[0].toUpperCase()}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{u.nome || 'Sem nome'}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email || 'Sem e-mail'}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip label={visual.label} color={visual.color} size="small" /></TableCell>
                    <TableCell>{formatDate(u.expiraEm)}</TableCell>
                    <TableCell>{formatDateTime(u.ultimoLoginEm)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" flexWrap="wrap">
                        <Tooltip title="Editar">
                          <span>
                            <IconButton size="small" onClick={() => abrirEditor(u)} disabled={emAcao}><Edit fontSize="small" /></IconButton>
                          </span>
                        </Tooltip>
                        <Button size="small" variant="outlined" onClick={() => gerenciarAcesso(u, { dias: 7 })} disabled={emAcao}>+7d</Button>
                        <Button size="small" variant="outlined" onClick={() => gerenciarAcesso(u, { dias: 30 })} disabled={emAcao}>+30d</Button>
                        <Button size="small" variant="outlined" color="success" onClick={() => gerenciarAcesso(u, { vitalicio: true })} disabled={emAcao}><AllInclusive fontSize="small" /></Button>
                        {u.status === 'bloqueado' ? (
                          <Button size="small" variant="outlined" color="success" onClick={() => gerenciarAcesso(u, { desbloquear: true })} disabled={emAcao}><Person /></Button>
                        ) : (
                          <Button size="small" variant="outlined" color="error" onClick={() => gerenciarAcesso(u, { bloquear: true })} disabled={emAcao}><PersonOff /></Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="xs">
        <DialogTitle>Editar cliente</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome" value={editData.nome} onChange={(e) => setEditData({ ...editData, nome: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth type="email" label="E-mail" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth type="date" label="Vencimento" value={editData.expiraEm} onChange={(e) => setEditData({ ...editData, expiraEm: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField select fullWidth label="Status" value={editData.status} onChange={(e) => setEditData({ ...editData, status: e.target.value })}>
            <MenuItem value="ativo">Ativo</MenuItem>
            <MenuItem value="bloqueado">Bloqueado</MenuItem>
            <MenuItem value="vitalicio">Vitalicio</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEdicaoManual} disabled={actionLoadingId === userSelecionado?.id}>
            Salvar alteracoes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} fullWidth maxWidth="sm">
        <DialogTitle>Detalhes do usuario</DialogTitle>
        <DialogContent dividers>
          {detailsUser ? (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">{detailsUser.nome || 'Sem nome'}</Typography>
              <Typography variant="body2" color="text.secondary">{detailsUser.email || 'Sem e-mail'}</Typography>

              <Grid container spacing={1} sx={{ mt: 1, mb: 1 }}>
                <Grid item xs={12} sm={6}><Typography variant="caption">Telefone: <strong>{detailsUser.telefone || '---'}</strong></Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption">Status: <strong>{statusVisual(detailsUser).label}</strong></Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption">Entrada: <strong>{detailsUser.horarioEntrada || '---'}</strong></Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="caption">Saida: <strong>{detailsUser.horarioSaida || '---'}</strong></Typography></Grid>
                <Grid item xs={12}><Typography variant="caption">Assinatura: <strong>{formatDate(detailsUser.expiraEm)}</strong></Typography></Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <History fontSize="small" /> Historico de entradas
              </Typography>

              {loadingLogs ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : userLogs.length > 0 ? (
                <List dense>
                  {userLogs.map((l, idx) => (
                    <React.Fragment key={l.id || `${l.data}-${idx}`}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={l.acao || l.tipo || 'Registro'}
                          secondary={formatDateTime(l.data)}
                        />
                      </ListItem>
                      {idx < userLogs.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">Sem historico disponivel.</Typography>
              )}
            </Box>
          ) : (
            <Typography>Carregando...</Typography>
          )}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Button onClick={() => setOpenDetails(false)}>Fechar</Button>
        </Box>
      </Dialog>
    </Container>
  );
}

export default Admin;
